import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, CATEGORIES, categoryName, timeAgoKannada } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Eye, Sparkles } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";

const emptyForm = {
  title: "",
  summary: "",
  content: "",
  category: "rajya",
  image_url: "",
  author: "ನಮಸ್ತೆ ಕರ್ನಾಟಕ ಡೆಸ್ಕ್",
  is_breaking: false,
  is_featured: false,
};

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/articles?limit=200");
        setArticles(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (authLoading) return <p className="text-muted-foreground">Loading...</p>;
  if (!user || user === false) return <Navigate to="/login" replace />;
  if (user.role !== "admin") {
    return (
      <div className="text-center py-24">
        <p className="text-muted-foreground">Admin access only.</p>
      </div>
    );
  }

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      title: a.title,
      summary: a.summary,
      content: a.content,
      category: a.category,
      image_url: a.image_url,
      author: a.author || "ನಮಸ್ತೆ ಕರ್ನಾಟಕ ಡೆಸ್ಕ್",
      is_breaking: !!a.is_breaking,
      is_featured: !!a.is_featured,
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.put(`/articles/${editing.id}`, form);
        setArticles((prev) => prev.map((a) => (a.id === data.id ? data : a)));
        toast.success("ಲೇಖನ ಅಪ್‌ಡೇಟ್ ಆಗಿದೆ");
      } else {
        const { data } = await api.post(`/articles`, form);
        setArticles((prev) => [data, ...prev]);
        toast.success("ಲೇಖನ ಸೇರಿಸಲಾಗಿದೆ");
      }
      setOpen(false);
    } catch (err) {
      toast.error("ಉಳಿಸಲಾಗಲಿಲ್ಲ");
    } finally {
      setSaving(false);
    }
  };

  const del = async (a) => {
    if (!window.confirm(`ಅಳಿಸಲು ಖಚಿತ: "${a.title}"?`)) return;
    try {
      await api.delete(`/articles/${a.id}`);
      setArticles((prev) => prev.filter((x) => x.id !== a.id));
      toast.success("ಅಳಿಸಲಾಗಿದೆ");
    } catch {
      toast.error("ಅಳಿಸಲಾಗಲಿಲ್ಲ");
    }
  };

  const deleteSeed = async () => {
    if (!window.confirm("ಎಲ್ಲಾ ಸ್ಯಾಂಪಲ್ ಸುದ್ದಿಗಳನ್ನು (Namaste Karnataka Desk) ಅಳಿಸಲು ಖಚಿತ? ನೀವು ಸೇರಿಸಿದ ಸ್ವಂತ ಸುದ್ದಿಗಳು ಸುರಕ್ಷಿತವಾಗಿರುತ್ತವೆ.")) return;
    try {
      const { data } = await api.post("/articles/seed/delete-all");
      toast.success(`${data.deleted} ಸ್ಯಾಂಪಲ್ ಸುದ್ದಿಗಳು ಅಳಿಸಲಾಗಿದೆ`);
      // Refresh list
      const { data: fresh } = await api.get("/articles?limit=200");
      setArticles(fresh);
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <div>
      <div className="flex items-baseline justify-between border-b-2 border-primary pb-4 mb-8">
        <div>
          <div className="font-ui text-xs uppercase tracking-[0.3em] text-muted-foreground">Admin</div>
          <h1 className="font-kannada-heading text-3xl mt-1">ಸುದ್ದಿ ನಿರ್ವಹಣೆ</h1>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" className="rounded-none" data-testid="settings-link">
            <Link to="/admin/settings">ಸೆಟ್ಟಿಂಗ್‌ಗಳು</Link>
          </Button>
          <Button asChild variant="outline" className="rounded-none" data-testid="admin-team-link">
            <Link to="/admin/team">ತಂಡ</Link>
          </Button>
          <Button asChild variant="outline" className="rounded-none" data-testid="admin-ads-link">
            <Link to="/admin/ads">ಜಾಹೀರಾತು</Link>
          </Button>
          <Button asChild variant="outline" className="rounded-none" data-testid="admin-payments-link">
            <Link to="/admin/payments">ಪಾವತಿಗಳು</Link>
          </Button>
          <Button asChild variant="outline" className="rounded-none" data-testid="admin-qrs-link">
            <Link to="/admin/qrs">QR ಕೋಡ್‌ಗಳು</Link>
          </Button>
          <Button
            onClick={deleteSeed}
            variant="outline"
            className="rounded-none border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground"
            data-testid="delete-seed-btn"
            title="Delete all sample (seeded) news articles"
          >
            <Sparkles className="w-4 h-4 mr-1" /> ಸ್ಯಾಂಪಲ್ ಅಳಿಸಿ
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} data-testid="new-article-btn" className="rounded-none">
              <Plus className="w-4 h-4 mr-1" /> ಹೊಸ ಸುದ್ದಿ
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-kannada-heading text-2xl">
                {editing ? "ಲೇಖನ ಸಂಪಾದಿಸಿ" : "ಹೊಸ ಲೇಖನ"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={save} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest">Title (ಶೀರ್ಷಿಕೆ)</Label>
                <Input
                  required
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="rounded-none mt-1"
                  data-testid="admin-title"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Summary (ಸಾರಾಂಶ)</Label>
                <Textarea
                  required
                  value={form.summary}
                  onChange={(e) => setForm({ ...form, summary: e.target.value })}
                  className="rounded-none mt-1"
                  rows={2}
                  data-testid="admin-summary"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Content (ವಿವರ)</Label>
                <Textarea
                  required
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  className="rounded-none mt-1"
                  rows={6}
                  data-testid="admin-content"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest">Category</Label>
                  <Select
                    value={form.category}
                    onValueChange={(v) => setForm({ ...form, category: v })}
                  >
                    <SelectTrigger className="rounded-none mt-1" data-testid="admin-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c.slug} value={c.slug}>
                          {c.name} · {c.en}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Author</Label>
                  <Input
                    value={form.author}
                    onChange={(e) => setForm({ ...form, author: e.target.value })}
                    className="rounded-none mt-1"
                    data-testid="admin-author"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Image URL</Label>
                <Input
                  required
                  value={form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  className="rounded-none mt-1"
                  data-testid="admin-image"
                  placeholder="https://..."
                />
                {form.image_url && (
                  <img src={form.image_url} alt="" className="mt-2 h-24 w-full object-cover rounded-sm" />
                )}
              </div>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={form.is_breaking}
                    onCheckedChange={(v) => setForm({ ...form, is_breaking: v })}
                    data-testid="admin-breaking"
                  />
                  <Label className="text-xs uppercase tracking-widest">Breaking News</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={form.is_featured}
                    onCheckedChange={(v) => setForm({ ...form, is_featured: v })}
                    data-testid="admin-featured"
                  />
                  <Label className="text-xs uppercase tracking-widest">Featured</Label>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none">
                  ರದ್ದು
                </Button>
                <Button type="submit" disabled={saving} className="rounded-none" data-testid="admin-save">
                  {saving ? "ಉಳಿಸುತ್ತಿದೆ..." : editing ? "ಅಪ್‌ಡೇಟ್" : "ಪ್ರಕಟಿಸಿ"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard label="ಒಟ್ಟು ಲೇಖನಗಳು" value={articles.length} />
        <StatCard label="ಬ್ರೇಕಿಂಗ್" value={articles.filter((a) => a.is_breaking).length} />
        <StatCard label="ಫೀಚರ್ಡ್" value={articles.filter((a) => a.is_featured).length} />
        <StatCard label="ಒಟ್ಟು ವೀಕ್ಷಣೆಗಳು" value={articles.reduce((s, a) => s + (a.views || 0), 0)} />
      </div>

      <div className="border border-border rounded-sm bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">Image</TableHead>
              <TableHead>Title</TableHead>
              <TableHead className="w-32">Category</TableHead>
              <TableHead className="w-24">Views</TableHead>
              <TableHead className="w-32">Time</TableHead>
              <TableHead className="w-32 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  Loading...
                </TableCell>
              </TableRow>
            ) : articles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  ಇಲ್ಲಿ ಲೇಖನಗಳಿಲ್ಲ.
                </TableCell>
              </TableRow>
            ) : (
              articles.map((a) => (
                <TableRow key={a.id} data-testid={`admin-row-${a.id}`}>
                  <TableCell>
                    <img src={a.image_url} alt="" className="w-12 h-10 object-cover rounded-sm" />
                  </TableCell>
                  <TableCell className="font-kannada-body">
                    <div className="flex items-center gap-2">
                      {a.is_breaking && (
                        <span className="text-[9px] bg-primary text-primary-foreground px-1.5 py-0.5 font-bold uppercase tracking-widest">
                          Breaking
                        </span>
                      )}
                      {a.is_featured && (
                        <span className="text-[9px] bg-secondary text-secondary-foreground px-1.5 py-0.5 font-bold uppercase tracking-widest">
                          Featured
                        </span>
                      )}
                    </div>
                    <div className="line-clamp-1 mt-1">{a.title}</div>
                  </TableCell>
                  <TableCell className="text-sm">{categoryName(a.category)}</TableCell>
                  <TableCell className="text-sm">{a.views || 0}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {timeAgoKannada(a.created_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" asChild data-testid={`admin-view-${a.id}`}>
                        <Link to={`/article/${a.id}`}>
                          <Eye className="w-4 h-4" />
                        </Link>
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => openEdit(a)}
                        data-testid={`admin-edit-${a.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => del(a)}
                        data-testid={`admin-delete-${a.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-primary" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="border border-border p-4 bg-card rounded-sm">
      <div className="font-ui text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{label}</div>
      <div className="font-kannada-heading text-3xl mt-1">{value}</div>
    </div>
  );
}
